import React, { useState, useEffect } from "react";
import ReactInputMask from "react-input-mask";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Box,
  Typography,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
} from "@mui/material";

import { apiGetFranchises } from "../../services/apiGetFranchises";
import { apiUpdateCommunityCreator } from "../../services/apiUpdateCommunityCreator";

import { ButtonFL } from "../ui/ButtonFL";
import { formatInstagramHandle, stripInstagramHandle } from "../../utils";

export const FranchiseSetupModal = ({ open, onClose, creator, communityId, onUpdate }) => {
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    selectedFranchise: null,
    franchiseInstagram: "",
    creatorInstagram: "",
    telefone: "",
  });
  const [isMissingFranchise, setIsMissingFranchise] = useState(false);

  useEffect(() => {
    if (open) {
      loadFranchises();
      setFormData({
        selectedFranchise: null,
        franchiseInstagram: creator.franchise?.instagram || "",
        creatorInstagram: formatInstagramHandle(creator?.instagram || ""),
        telefone: creator?.telefone || "",
      });
      setIsMissingFranchise(false);
    }
  }, [open, creator]);

  const loadFranchises = async () => {
    try {
      setLoading(true);
      const response = await apiGetFranchises(communityId);
      setFranchises(response || []);
    } catch (error) {
      setError("Erro ao carregar unidades. Tente novamente.");
      console.error("Error loading franchises:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    let processedValue = value;

    if (field === "franchiseInstagram" || field === "creatorInstagram") {
      processedValue = formatInstagramHandle(value);
    }

    setFormData(prev => ({
      ...prev,
      [field]: processedValue
    }));
    if (error) setError("");
  };

  const validateForm = () => {
    if (!isMissingFranchise && !formData.selectedFranchise) {
      setError("Por favor, selecione uma unidade ou marque que não encontrou sua unidade.");
      return false;
    }
    if (!formData.telefone?.trim()) {
      setError("Por favor, preencha o número de telefone.");
      return false;
    }
    return true;
  };

  const buildFranchiseData = () => {
    const franchiseInstagram = stripInstagramHandle(formData.franchiseInstagram?.trim()) || null;

    if (formData.selectedFranchise) {
      return {
        id: formData.selectedFranchise.id,
        instagram: franchiseInstagram,
      };
    }

    if (isMissingFranchise) {
      return {
        instagram: franchiseInstagram,
      };
    }

    return null;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      setError("");

      const updateData = {
        franchise: buildFranchiseData(),
        instagram: stripInstagramHandle(formData.creatorInstagram?.trim()) || null,
        telefone: formData.telefone.trim(),
      };

      const updatedCreator = await apiUpdateCommunityCreator(communityId, creator.id, updateData);

      if (onUpdate) {
        onUpdate(updatedCreator);
      }

      onClose();
    } catch (error) {
      setError("Erro ao salvar informações. Tente novamente.");
      console.error("Error updating creator:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={submitting}
    >
      <DialogTitle>
        <Typography variant="h6" component="div">
          Configuração de Unidade
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
            Para continuar usando a plataforma, você precisa configurar sua unidade e
            atualizar suas informações de contato.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Franchise Selection */}
            <Autocomplete
              options={franchises}
              getOptionLabel={(option) => option.name || ""}
              value={formData.selectedFranchise}
              onChange={(event, newValue) => {
                handleInputChange("selectedFranchise", newValue);
                if (newValue) {
                  setIsMissingFranchise(false);
                }
              }}
              loading={loading}
              disabled={submitting || isMissingFranchise}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Unidade *"
                  placeholder="Selecione sua unidade"
                  helperText={isMissingFranchise ? "Desabilitado - você marcou que não encontrou sua unidade" : "Obrigatório - Selecione sua unidade"}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {/* Missing Franchise Checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={isMissingFranchise}
                  onChange={(e) => {
                    setIsMissingFranchise(e.target.checked);
                    if (e.target.checked) {
                      handleInputChange("selectedFranchise", null);
                    }
                  }}
                  disabled={submitting}
                />
              }
              label="Não encontrei minha unidade na lista"
            />

            {/* Franchise Instagram */}
            <TextField
              label="Instagram da unidade"
              placeholder="@unidade"
              value={formData.franchiseInstagram}
              onChange={(e) => handleInputChange("franchiseInstagram", e.target.value)}
              disabled={submitting || (!formData.selectedFranchise && !isMissingFranchise)}
              helperText={
                !formData.selectedFranchise && !isMissingFranchise
                  ? "Selecione uma unidade primeiro ou marque que não encontrou sua unidade"
                  : "Opcional - Instagram específico desta unidade"
              }
            />


            {/* Phone Number */}
            <ReactInputMask mask="(99) 99999-9999"
              value={formData.telefone}
              onChange={(e) => handleInputChange("telefone", e.target.value)}
              disabled={submitting}
            >
              {(inputProps) => (
                <TextField
                  {...inputProps}
                  label="Seu Telefone *"
                  placeholder="(11) 99999-9999"
                  helperText="Obrigatório - Seu número de telefone para contato"
                />
              )}
            </ReactInputMask>

            {/* Creator Instagram */}
            <TextField
              label="Seu Instagram"
              placeholder="@seu_instagram"
              value={formData.creatorInstagram}
              onChange={(e) => handleInputChange("creatorInstagram", e.target.value)}
              disabled={submitting}
              helperText="Opcional - Seu Instagram pessoal"
            />

          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <ButtonFL
          onClick={handleClose}
          disabled={submitting}
          bgcolor="var(--grey-100-color)"
          textColor="var(--primary-text-color)"
        >
          Fechar
        </ButtonFL>
        <ButtonFL
          onClick={handleSubmit}
          disabled={submitting || loading}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
        >
          {submitting ? "Salvando..." : "Salvar"}
        </ButtonFL>
      </DialogActions>
    </Dialog>
  );
};
